// CommandPalette — ⌘K. Driven by useUiStore.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUiStore } from "@/store/ui";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/cn";

interface Command {
  id: string;
  label: string;
  section: "Navigate" | "Actions" | "Records" | "Recent";
  hint?: string;
  run: () => void;
}

export function CommandPalette() {
  const open = useUiStore(s => s.commandOpen);
  const setOpen = useUiStore(s => s.setCommandOpen);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const commands: Command[] = useMemo(() => [
    { id: "nav-home", label: "Go to Dashboard", section: "Navigate", hint: "g h", run: () => navigate("/home") },
    { id: "nav-browse", label: "Browse instruments", section: "Navigate", hint: "g b", run: () => navigate("/browse/instruments") },
    { id: "nav-approvals", label: "Pending approvals", section: "Navigate", hint: "g a", run: () => navigate("/approvals") },
    { id: "nav-mappings", label: "Mappings & crosswalks", section: "Navigate", run: () => navigate("/mappings") },
    { id: "nav-quality", label: "Quality rules", section: "Navigate", run: () => navigate("/quality") },
    { id: "nav-imports", label: "Imports", section: "Navigate", run: () => navigate("/imports") },
    { id: "nav-lineage", label: "Lineage", section: "Navigate", run: () => navigate("/lineage") },
    { id: "nav-hier", label: "Hierarchies", section: "Navigate", run: () => navigate("/hierarchies") },
    { id: "nav-audit", label: "Audit log", section: "Navigate", run: () => navigate("/audit") },
    { id: "nav-settings", label: "Settings", section: "Navigate", run: () => navigate("/settings") },
    { id: "act-new-record", label: "New record…", section: "Actions", hint: "c", run: () => navigate("/browse/instruments") },
    { id: "act-import", label: "Import CSV…", section: "Actions", run: () => navigate("/imports") },
    { id: "act-export", label: "Export current view…", section: "Actions", run: () => { /* noop */ } },
  ], [navigate]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setActive(0);
    setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!open);
      } else if (open && e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return commands;
    return commands.filter(c => c.label.toLowerCase().includes(term));
  }, [q, commands]);

  const grouped = useMemo(() => {
    const map = new Map<Command["section"], Command[]>();
    for (const c of filtered) {
      const arr = map.get(c.section) ?? [];
      arr.push(c);
      map.set(c.section, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  if (!open) return null;

  const runActive = () => {
    const cmd = filtered[active];
    if (cmd) { cmd.run(); setOpen(false); }
  };

  return (
    <div className="lex-cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
      <div className="lex-cmdk__scrim" onClick={() => setOpen(false)} aria-hidden="true" />
      <div className="lex-cmdk__panel" onKeyDown={(e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => Math.min(filtered.length - 1, i + 1)); }
        if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
        if (e.key === "Enter") { e.preventDefault(); runActive(); }
      }}>
        <div className="lex-cmdk__search">
          <Icon.Search size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            placeholder="Search commands and records…"
            aria-label="Command search"
          />
          <kbd className="lex-cmdk__esc">ESC</kbd>
        </div>
        <div className="lex-cmdk__list">
          {filtered.length === 0 ? (
            <div className="lex-cmdk__empty">No results.</div>
          ) : grouped.map(([section, items]) => (
            <div key={section} className="lex-cmdk__group">
              <div className="lex-cmdk__heading">{section}</div>
              {items.map(cmd => {
                const idx = filtered.indexOf(cmd);
                const isActive = idx === active;
                return (
                  <button
                    key={cmd.id}
                    type="button"
                    className={cn("lex-cmdk__item", isActive && "lex-cmdk__item--active")}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => { cmd.run(); setOpen(false); }}
                  >
                    <span>{cmd.label}</span>
                    {cmd.hint ? <span className="lex-cmdk__hint">{cmd.hint}</span> : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
